import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";

import getSchema from "../../lib/api/getSchema";
import notificationState from "../../states/notificationState";
import AccessControl from "../functional/AccessControl";
import DeleteRecordButton from "../input/DeleteRecordButton";
import SaveToExcelButton from "../input/SaveToExcelButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import UserExcelUploadButton from "../input/UserExcelUploadButton";
import UserExcelFormDownloadButton from "../input/UserExcelFormDownloadButton";
import UserCreateButton from "../input/UserCreateButton";

import { cloneDeep } from "lodash";

function UserManage(props) {
    const [schema, setSchema] = useState();
    const [Api, setApi] = useState();
    const [grid, setGrid] = useState();
    const [modifiedSchema, setModefiedSchema] = useState();

    const setNotification = useSetRecoilState(notificationState);

    const { t } = useTranslation();

    useEffect(() => {
        let mounted = true;

        (async () => {
            let schema = await getSchema("User");

            if(mounted) {
                schema.columns.forEach(column => schemaColumnSetting(column));
                setSchema(schema);

                let modifiedSchema = cloneDeep(schema);
                modifiedSchema.formGroups = ["form.userForm.groupName.excelFile"];
                modifiedSchema.columns.forEach(column => columnSetting(column));
                setModefiedSchema(modifiedSchema);
            }

            function schemaColumnSetting(column) {
                if(column.name === "excelFile") {
                    column.hideInForm = true;
                    column.required = false;
                }
            }
            
            function columnSetting(column) {
                if(column.key === "PRI") {
                    column.defaultValue = "pri";
                }
    
                if(column.name !== "excelFile") {
                    column.hideInForm = true;
                    column.required = false;
                } else {
                    column.hideInForm = false;
                }
            }
        })();

        return () => {
            mounted = false;
        }
    }, []);

    const onApiInitialized = (api) => {
        setApi(api);
    };

    const onInitialized = (grid) => {
        setGrid(grid);
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
    };

    const onFailure = (reason) => {
        notifyFailure(reason.response ? reason.response.data : reason);
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
    };
    
    const onModifyUrl = (url) => {
        return `${url}?excelData`;
    };

    return (
        <>
            <BasicCrudScreenType 
                id="User" 
                table="User" 
                view="VwUser" 
                onApiInitialized={onApiInitialized}
                onInitialized={onInitialized}
                noCreate
                noEdit
                noDelete
                noExcel
                onAppendButtonInFrontOfSearch={() => (
                    <>
                        <BasicCrudScreenActionItemLayout>
                            <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                <UserExcelUploadButton
                                    id="userExcelUploadButton"
                                    createButtonName="excelUpload"
                                    schema={modifiedSchema}
                                    refresh={Api}
                                    onSuccess={onSuccess}
                                    modifyUrlCallback={(url) => onModifyUrl(url)}
                                />
                            </AccessControl>

                            <AccessControl accessTarget={schema && schema.name} accessMethod="GET">
                                <UserExcelFormDownloadButton
                                    id = "userExcelFormDownloadButton"
                                    schema = {schema}
                                    refresh = {Api}
                                    grid = {grid}
                                    onSuccess = {onSuccess}
                                />
                            </AccessControl>

                            <AccessControl
                                accessTarget={schema && schema.name}
                                accessMethod="POST"
                            >
                                <UserCreateButton
                                    id="userCreateButton"
                                    schema={schema}
                                    refresh={Api}
                                    onSuccess={onSuccess}
                                />
                            </AccessControl>

                            <AccessControl
                                accessTarget={schema && schema.name}
                                accessMethod={
                                    schema &&
                                    (schema.updateWithPatch ? "PATCH" : "PUT")
                                }
                            >
                                <UpdateRecordButton
                                    id="userUpdateButton"
                                    schema={schema}
                                    grid={grid}
                                    refresh={Api}
                                    onSuccess={onSuccess}
                                />
                            </AccessControl>

                            <AccessControl
                                accessTarget={schema && schema.name}
                                accessMethod="DELETE"
                            >
                                <DeleteRecordButton
                                    id="userDeleteButton"
                                    schema={schema}
                                    grid={grid}
                                    refresh={Api}
                                    color="secondary"
                                    onSuccess={onSuccess}
                                    onFailure={onFailure}
                                    onPartialFailure={onNotifyWarning}
                                />
                            </AccessControl>

                            <AccessControl
                                accessTarget={schema && schema.name}
                                accessMethod="SAVETOEXCEL"
                            >
                                <SaveToExcelButton
                                    id="User"
                                    grid={grid}
                                    view="VwUser"
                                    color="primary"
                                />
                            </AccessControl>
                        </BasicCrudScreenActionItemLayout>
                    </>
                )}
            />
        </>
    );
}

export default UserManage;

